import { useRef, useState } from 'react';
import useSWR from 'swr';
import { useTranslation } from '@yoweb/next-i18next';
import { Box } from '@yoweb/ui/components/Box';
import { FlyoutHeroLayout } from '@poplar/components/Flyout/FlyoutTodo';
import { API_TASK_TEMPLATE_CATEGORY_ID } from '@poplar/constants/routes';
import { fetcher } from '@yoweb/utils/common';
import { LoadingPage } from '@yoweb/ui/components/LoadingPage';
import { ErrorSubstate } from '@poplar/components/ErrorSubstate';
import { TextSeperator } from '@poplar/components/TextSeperator';
import { Flex } from '@yoweb/ui/components/Flex';
import { Button } from '@yoweb/ui/components/buttons';
import { SubCategoryCard } from '@poplar/components/TaskTemplate/SubCategoryCard';
import { CardCarousel } from '@yoweb/ui/components/CardCarousel';
import { FeatureCard } from '@poplar/components/TaskTemplate/FeatureCard';
import { theme } from '@yoweb/ui/styles';
import { trackEvent } from '@yoweb/utils/analytics/mixpanel';
import { TodoType, TodoEntryPoint } from '@yoweb/types/external-events';
import { useUser } from '@poplar/hooks/useUser';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var SubCategoryInner = function SubCategoryInner(_ref) {
  var data = _ref.data,
      error = _ref.error,
      isLoading = _ref.isLoading,
      onSubCategoryClick = _ref.onSubCategoryClick;

  if (error) {
    return /*#__PURE__*/_jsx(ErrorSubstate, {
      error: error
    });
  }

  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingPage, {});
  }

  return /*#__PURE__*/_jsx(_Fragment, {
    children: (data === null || data === void 0 ? void 0 : data.subcategoryListCardsList) && /*#__PURE__*/_jsx(Box, {
      display: "grid",
      gap: "small4",
      children: data.subcategoryListCardsList.map(function (card) {
        return /*#__PURE__*/_jsx(SubCategoryCard, {
          card: card,
          onClick: function onClick() {
            onSubCategoryClick(card.taskTemplateId, !card.skipPdp);
            trackEvent({
              todoStart: {
                title: card.name,
                entryPoint: TodoEntryPoint.TODO_ENTRY_POINT_CATEGORY_SUBCATEGORY,
                type: TodoType.TODO_TYPE_B
              }
            });
          }
        }, card.id);
      })
    })
  });
};

var HeroHeaderContent = function HeroHeaderContent(_ref2) {
  var data = _ref2.data,
      onSubCategoryClick = _ref2.onSubCategoryClick,
      externalParentRef = _ref2.externalParentRef;

  var _useState = useState(false),
      isDragging = _useState[0],
      setIsDragging = _useState[1];

  return /*#__PURE__*/_jsx(Box, {
    position: "relative",
    zIndex: theme.zIndices.above,
    children: /*#__PURE__*/_jsx(CardCarousel, {
      id: "featured-card-carousel",
      animatedIn: false,
      cardGap: 12,
      externalParentRef: externalParentRef,
      onIsDragging: function onIsDragging(isCarouselDragging) {
        return setIsDragging(isCarouselDragging);
      },
      itemWidth: {
        _: 300,
        sm: 480
      },
      hasLeftPadding: true,
      children: data.mediumCardsList.map(function (card) {
        return /*#__PURE__*/_jsx(FeatureCard, {
          card: card,
          cursor: isDragging ? 'grabbing' : 'pointer',
          onClick: function onClick() {
            if (!isDragging) {
              onSubCategoryClick(card.taskTemplateId, !card.skipPdp);
              trackEvent({
                todoStart: {
                  title: card.title,
                  entryPoint: TodoEntryPoint.TODO_ENTRY_POINT_CATEGORY_FEATURE,
                  type: TodoType.TODO_TYPE_A
                }
              });
            }
          }
        }, card.taskTemplateId);
      })
    })
  });
};

export var SubCategory = function SubCategory(_ref3) {
  var onSlideClose = _ref3.onSlideClose,
      onSlideBack = _ref3.onSlideBack,
      categoryId = _ref3.categoryId,
      onCreateCustomClicked = _ref3.onCreateCustomClicked,
      onSubCategoryClick = _ref3.onSubCategoryClick;

  var _useUser = useUser(),
      isAuthenticated = _useUser.isAuthenticated;

  var _useSWR = useSWR(isAuthenticated ? API_TASK_TEMPLATE_CATEGORY_ID(categoryId) : undefined, fetcher),
      data = _useSWR.data,
      error = _useSWR.error,
      isLoading = _useSWR.isLoading;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var flyoutHeroLayoutRef = useRef(null);
  return /*#__PURE__*/_jsxs(FlyoutHeroLayout, {
    title: data === null || data === void 0 ? void 0 : data.categoryName,
    heroImage: data === null || data === void 0 ? void 0 : data.categoryHeroImageUrl,
    "data-testid": "sub-category-layout",
    onClose: onSlideClose,
    onBack: onSlideBack,
    heroHeaderContent: data !== null && data !== void 0 && data.mediumCardsList && data.mediumCardsList.length > 0 ? /*#__PURE__*/_jsx(HeroHeaderContent, {
      data: data,
      onSubCategoryClick: onSubCategoryClick,
      externalParentRef: flyoutHeroLayoutRef
    }) : undefined,
    ref: flyoutHeroLayoutRef,
    children: [/*#__PURE__*/_jsx(SubCategoryInner, {
      data: data,
      error: error,
      isLoading: isLoading,
      onSubCategoryClick: onSubCategoryClick
    }), /*#__PURE__*/_jsx(Box, {
      mt: "normal3",
      children: /*#__PURE__*/_jsx(TextSeperator, {
        text: t('todo-intake.or-divider')
      })
    }), /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(Box, {
        mt: "normal3",
        children: /*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          size: "lg",
          type: "button",
          onClick: onCreateCustomClicked,
          "data-testid": "create-todo-custom-todo-button",
          children: t('todo-intake.create-type-c')
        })
      })
    })]
  });
};